import { type ReactNode } from 'react';
import { Box } from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';
import { MasonryScreenLayout } from '@leagueplatform/web-common-components';
import { SECONDARY_CONTENT_MAX_WIDTH } from 'common/constants';
import { hiddenMasonryNodeStyles } from 'common/plan-switcher-masonry-styles';
import type { LocalesKey } from 'types/types';

type PlanSwitcherChromeProps = {
  // Analytics screen name reported for the surface. Differs per entry
  // point ('Switch Accounts' for the Settings route, 'Your Plans' for the
  // onboarding gate), so it stays a prop while the visible heading is constant.
  screenName: string;
  // App locales key used for the document <title> (also differs per entry point).
  titleMessageId: LocalesKey;
  // The plan-switcher Masonry surface rendered inside the bordered card.
  children: ReactNode;
};

// Chrome for the Plan Switcher screen: a constant "Your Plans" heading over a
// bordered content card wrapping the server-driven Masonry surface. Shared by
// the Switch Accounts page and the onboarding gate so the heading copy is
// defined once; each entry point supplies its own analytics screen name and
// document title.
export const PlanSwitcherChrome = ({
  screenName,
  titleMessageId,
  children,
}: PlanSwitcherChromeProps) => {
  const { formatMessage } = useIntl();

  return (
    <MasonryScreenLayout
      heading={formatMessage({ id: 'STR_YOUR_PLANS' })}
      screenName={screenName}
      titleMessageId={titleMessageId}
      bordered
      maxWidth={SECONDARY_CONTENT_MAX_WIDTH}
    >
      <Box
        // No subtitle alignment rule here, so the server-driven left alignment
        // stands (the gate centers it on desktop; this surface does not).
        // Collapse the phantom stack gaps left by hidden Masonry nodes
        // (selectionError + plan-count field); shared with PlanSwitcherGate so
        // they can't drift.
        css={hiddenMasonryNodeStyles}
      >
        {children}
      </Box>
    </MasonryScreenLayout>
  );
};
